import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDownRight, ArrowUpRight, Minus, TrendingUp } from 'lucide-react';

import { useMetaCardsQuery } from '@/services/qbotApi';
import { CardIcon } from '@/components/cr/CardIcon';
import { UnderlineTabs } from '@/components/common/UnderlineTabs';
import type { CRCBestCard } from '@/types/qbot';

/** Selectable trailing windows for the meta aggregation. */
const WINDOWS = ['1d', '3d', '7d', '30d'] as const;
type MetaWindow = (typeof WINDOWS)[number];

/** Usage-change magnitude (in percentage points) below which we call it "flat". */
const FLAT_EPSILON = 0.1;

/**
 * "Meta trend" — the GLOBAL best-cards ranking (not the player's collection),
 * with a risers/fallers movement indicator per card. Reads the best-effort
 * `/meta/cards` endpoint, which returns `{ cards: [] }` on any upstream hiccup —
 * so an empty payload is a normal empty state, never an error, and there's no
 * linked-tag / NOT_LINKED handling here.
 *
 * A small window selector (1d/3d/7d/30d) drives the query; the ranked list is
 * its own scroller so it brings its own `overflow-y-auto`.
 */
export function TrendMetaMode() {
  const { t } = useTranslation('cr');
  const [window, setWindow] = useState<MetaWindow>('7d');

  // `currentData` (not `data`): while switching windows RTK's `data` keeps the
  // PREVIOUS window's cards until the new request resolves, so the ranking
  // flashed the old range's order — worst on returning to the first window.
  // `currentData` is undefined during that transition → we show the skeleton,
  // never a stale ranking.
  //
  // refetchOnReconnect:false — each window is fetched once and served from cache
  // for the whole session (keepUnusedDataFor 30 min). Without this a network
  // flap silently refetches and, since the backend ranking can shift between
  // calls, the list "doesn't come back the same" when you revisit a range.
  const { currentData, isFetching } = useMetaCardsQuery({ window }, { refetchOnReconnect: false });
  // The raw payload is rough — normalise it before rendering:
  //  - sort by the `rank` field (it isn't the array order, and it isn't a
  //    sequential 1..N either — several cards share a value), then show the
  //    sequential POSITION (1,2,3…) so the list never shows #1,#1,#3,#3;
  //  - de-dupe by card id (the endpoint repeats the same card);
  //  - rescale 0-1 rates to percentages (win/usage/cwr come as fractions:
  //    0.5 → 50%, 0.12 → 12%; deltas too). `Math.abs(v) <= 1` leaves an
  //    already-percentage value untouched.
  const cards = useMemo(() => {
    // ⚠️ IL `null` PASSA, E DEVE PASSARE. La guardia `typeof v === 'number'` esclude
    // `null` e `undefined`, che finiscono nel `return v`: l'assente resta assente
    // invece di diventare un numero. Fino al 2026-09-01 questo accadeva per
    // ACCIDENTE — la firma diceva `(v?: number)` e nessuno l'aveva scritto apposta —
    // e il `?? 0` due righe sotto lo annullava comunque. Ora e' deliberato, e la
    // firma lo dice.
    const pct = (v: number | null | undefined): number | null | undefined =>
      typeof v === 'number' && Math.abs(v) <= 1 ? v * 100 : v;
    const sorted = [...(currentData?.cards ?? [])].sort((a, b) => a.rank - b.rank);
    const seen = new Set<number>();
    return sorted
      .filter((c) => (seen.has(c.card.id) ? false : (seen.add(c.card.id), true)))
      .map((c) => ({
        ...c,
        stats: {
          ...c.stats,
          // Questi due `?? 0` restano, e la ragione e' MISURATA invece che assunta:
          // `winRate` e `usageRate` non possono arrivare `null`, con due difese
          // indipendenti nel sorgente upstream — `winRate` esce da un `$cond` il cui
          // ramo *else* e' `0` e non null, `usageRate` e' una divisione col
          // denominatore protetto da `$max(…, 1)`. Non e' «di fatto non capita»: non
          // esiste la strada. (Lettura di qcrc-orc su `BestCardsPipeline.cs` @ `a61a14a`,
          // 2026-09-01.)
          //
          // Quindi la nota rossa qui sotto non e' contraddetta da queste due righe: la
          // regola vale per l'ESPRESSIONE `?? 0` applicata a un dato che puo' mancare, e
          // qui non c'e' nessun assente da difendere.
          //
          // ⚠️ MA DA OGGI SONO PORTANTI PER IL TIPO, e prima non lo erano. Allargando
          // `pct` a `number | null | undefined` — per lasciar passare l'assente dei
          // delta — senza questi `?? 0` il file non compila piu'. Chi ci arrivera'
          // trovera' quindi un `?? 0` che il COMPILATORE pretende, che e' l'argomento
          // piu' convincente per lasciarlo dov'e'. Ma l'obbligo nasce dal nostro
          // allargamento di `pct`, non dal dominio: se un giorno il backend cambiasse,
          // questi due diventerebbero esattamente il caso di `usageChange` — e il
          // compilatore, contento com'e', non lo direbbe a nessuno.
          // (Rilievo di tma-bg-orcr, 2026-09-01.)
          winRate: pct(c.stats.winRate) ?? 0,
          usageRate: pct(c.stats.usageRate) ?? 0,
          // `?? undefined` solo per soddisfare il tipo (`cwr?: number`) dopo che `pct`
          // ammette `null`: comportamento invariato, perche' il render di `cwr` e' gia'
          // dietro `typeof stats.cwr === 'number'` e un `null` non passava comunque.
          cwr: pct(c.stats.cwr) ?? undefined,
          // 🔴 QUI C'ERA `?? 0`, E NON DEVE TORNARE.
          //
          // Il delta e' `null` quando il campione della finestra precedente e' sotto
          // soglia: colmarlo con zero trasforma «non lo sappiamo» in «variazione
          // nulla», che e' un'affermazione diversa e falsa. Lo zero cadeva poi sotto
          // `FLAT_EPSILON`, quindi un dato ASSENTE rendeva lo stesso pixel di un dato
          // PIATTO — e non poteva accorgersene nessuno, ne' l'utente ne' noi.
          //
          // ⚠️ Il `?? null` qui sotto NON e' la stessa cosa e non reintroduce il difetto:
          // normalizza `undefined` (chiave assente) su `null` (valore assente), cioe'
          // manda i due modi di non-sapere sullo stesso ramo. Assente resta assente; e'
          // il *numero* che non deve comparire.
          //
          // Rimettere un `?? 0` compila senza un solo rosso e annulla tutto questo — per
          // questo la nota sta accanto alla riga e non solo nel messaggio di commit.
          usageChange: pct(c.stats.usageChange) ?? null,
          winChange: pct(c.stats.winChange) ?? null,
        },
      }));
  }, [currentData]);

  return (
    <div className="flex h-full flex-col gap-2">
      {/*
        Il selettore delle finestre prende una RIGA SUA. Prima stava schiacciato
        a destra del titolo: quattro tasti da 10px in mezza riga, sotto qualunque
        soglia comoda per un dito, e con l'attivo indistinguibile a colpo
        d'occhio dagli altri tre.

        Lo spazio arriva dalla descrizione, rimossa: diceva «migliori carte e
        movimenti del momento», cioè ripeteva a parole quello che la tabella
        sotto mostra già. Costo netto in altezza: zero.
      */}
      <div className="flex shrink-0 flex-col gap-1.5">
        <h2 className="flex min-w-0 items-center gap-1.5 text-sm font-semibold text-fg">
          <TrendingUp className="h-4 w-4 shrink-0 text-brand" />
          <span className="truncate">{t('panel.trend.title')}</span>
        </h2>
        {/* Sottolineatura e NON segmentato: qui sopra c'è già il segmentato dei
            Decks, e due contenitori pieni incolonnati si somigliano troppo —
            non si capirebbe più quale dei due comanda cosa. Questo è un
            selettore secondario e deve pesare di meno. */}
        <UnderlineTabs
          value={window}
          onChange={setWindow}
          options={WINDOWS.map((w) => ({ value: w, label: t(`panel.trend.window.${w}`) }))}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-0.5">
        {isFetching && cards.length === 0 ? (
          <TrendSkeleton />
        ) : cards.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center px-4 text-center">
            <p className="max-w-[16rem] text-xs text-fg-muted">{t('panel.trend.empty')}</p>
          </div>
        ) : (
          <ul className="space-y-1.5">
            {cards.map((c, i) => (
              <TrendRow key={c.card.id} position={i + 1} card={c} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function TrendRow({ card, position }: { card: CRCBestCard; position: number }) {
  const { t } = useTranslation('cr');
  const { card: c, stats } = card;
  return (
    <li className="flex items-center gap-2.5 rounded-xl bg-bg-secondary p-2">
      <span className="w-5 shrink-0 text-center text-xs font-bold tabular-nums text-fg-muted">
        {position}
      </span>
      <div className="w-9 shrink-0">
        <CardIcon id={c.id} name={c.name} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-xs font-semibold text-fg">{c.name}</span>
        <div className="flex items-center gap-2 text-[10px] tabular-nums text-fg-muted">
          <span>
            {t('panel.trend.wr')} {stats.winRate.toFixed(1)}%
          </span>
          <span>
            {t('panel.trend.use')} {stats.usageRate.toFixed(1)}%
          </span>
          {typeof stats.cwr === 'number' && (
            <span className="text-brand">
              {t('panel.trend.cwr')} {stats.cwr.toFixed(1)}%
            </span>
          )}
        </div>
      </div>
      <TrendChange change={stats.usageChange} />
    </li>
  );
}

/**
 * L'indicatore di movimento — TRE stati, non due.
 *
 * ✅ DECISIONE PRESA (Luca, 2026-09-02, opzione «b»): «distingui i due stati». Il ramo
 * `null` non rende piu' il trattino. Prima del refactor del 01/09 non poteva farlo — il
 * `?? 0` al call site trasformava l'assente in zero e lo zero cadeva sotto
 * `FLAT_EPSILON` — quindi «non lo sappiamo» e «non e' cambiato niente» erano
 * letteralmente lo stesso pixel. Ora sono due cose:
 *
 *   `> FLAT_EPSILON`   → freccia su, verde, col numero      «e' salita»
 *   `< -FLAT_EPSILON`  → freccia giu', rossa, col numero    «e' scesa»
 *   `|…| <= EPSILON`   → trattino                            «e' rimasta piatta» ← MISURA
 *   `null`             → «n.d.»                              «non lo sappiamo»  ← ASSENZA
 *
 * ⚠️ E LA DIFFERENZA DEVE VEDERSI SENZA PASSARE IL DITO SOPRA. Niente `title`, niente
 * tooltip: questa e' una Mini App dentro Telegram, su un telefono **l'hover non esiste**
 * — un significato affidato a un tooltip qui non e' «meno accessibile», e' assente. Per
 * questo la distinzione e' di FORMA (un'icona contro del testo) e non di sfumatura.
 *
 * ⚠️ E NON DEVE SEMBRARE UN GUASTO. Su `custom_30d` il delta manca per **113 righe su
 * 177** (63,8%, misura di qcrc-orc) — e con tutte le altre statistiche piene accanto.
 * Quindi niente colore d'allarme, niente icona di avviso: lo stesso grigio del trattino,
 * perche' li' non e' un'eccezione, e' lo stato normale di quel segmento. Una riga con
 * `WR 54.2%` e `n.d.` accanto deve leggersi come informazione incompleta, non come
 * qualcosa che si e' rotto.
 */
function TrendChange({ change }: { change: number | null }) {
  const { t } = useTranslation('cr');
  // Prima del confronto, e non insieme: un `null` in un `>` non e' un errore in
  // JavaScript, e' un `false` silenzioso — che darebbe la risposta giusta per il
  // motivo sbagliato, cioe' il modo migliore di perderla al primo cambio di soglia.
  if (change === null) {
    return (
      <span className="shrink-0 text-[10px] font-semibold tabular-nums text-fg-muted">
        {t('panel.trend.unknown')}
      </span>
    );
  }
  if (change > FLAT_EPSILON) {
    return (
      <span className="inline-flex shrink-0 items-center gap-0.5 text-[10px] font-bold tabular-nums text-emerald-400">
        <ArrowUpRight className="h-3 w-3" />+{change.toFixed(1)}%
      </span>
    );
  }
  if (change < -FLAT_EPSILON) {
    return (
      <span className="inline-flex shrink-0 items-center gap-0.5 text-[10px] font-bold tabular-nums text-rose-300">
        <ArrowDownRight className="h-3 w-3" />
        {change.toFixed(1)}%
      </span>
    );
  }
  return (
    <span className="shrink-0 text-fg-muted">
      <Minus className="h-3 w-3" />
    </span>
  );
}

function TrendSkeleton() {
  return (
    <ul className="space-y-1.5">
      {Array.from({ length: 7 }).map((_, i) => (
        <li key={i} className="flex items-center gap-2.5 rounded-xl bg-bg-secondary p-2">
          <div className="h-9 w-9 shrink-0 animate-pulse rounded-md bg-bg-tertiary" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 w-2/3 animate-pulse rounded bg-bg-tertiary" />
            <div className="h-2 w-1/2 animate-pulse rounded bg-bg-tertiary" />
          </div>
        </li>
      ))}
    </ul>
  );
}
